import React, { useState } from "react";
import {
  Box, Stack, Typography, Paper, Chip, IconButton, Collapse, Button, FormControl, Select, MenuItem, CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { QuizStyledCard } from "../../shared/QuizStyledCard";
import { COLORS, PACK_SIZES } from "../constants";
import type { SubscriptionData, SubIssues, ModalType } from "../types";

interface Props {
  subsWithIssues: Array<{ sub: SubscriptionData; issues: SubIssues }>;
  onOpenModal: (type: ModalType, sub: SubscriptionData) => void;
  onQuickFixMismatch: (sub: SubscriptionData, targetPackSize: number) => Promise<void>;
}

export function DataIssuesSection({ subsWithIssues, onOpenModal, onQuickFixMismatch }: Props) {
  const [show, setShow] = useState(true);
  const [fixSelections, setFixSelections] = useState<Record<string, string>>({});
  const [fixingSubId, setFixingSubId] = useState<string | null>(null);

  if (subsWithIssues.length === 0) return null;

  const handleFix = async (sub: SubscriptionData) => {
    const targetSize = parseInt(fixSelections[sub.id], 10);
    if (!targetSize) return;
    setFixingSubId(sub.id);
    try {
      await onQuickFixMismatch(sub, targetSize);
      setFixSelections(prev => { const next = { ...prev }; delete next[sub.id]; return next; });
    } finally {
      setFixingSubId(null);
    }
  };

  return (
    <QuizStyledCard>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }} onClick={() => setShow(!show)}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon sx={{ color: COLORS.red }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: COLORS.brownDeep }}>
            Data Issues ({subsWithIssues.length})
          </Typography>
        </Box>
        <IconButton size="small">
          {show ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      </Box>

      <Collapse in={show} timeout="auto" unmountOnExit>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {subsWithIssues.map(({ sub, issues }) => {
            const planDays = sub.properties?.find(p => p.name === "_paws_plan_days")?.value || "?";
            const packSize = sub.properties?.find(p => p.name === "_paws_pack_size_grams")?.value || "?";

            return (
              <Paper key={sub.id} sx={{ p: 2, borderRadius: "12px", border: `1px solid ${COLORS.redBorder}`, bgcolor: COLORS.redLighter }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: COLORS.brownDeep, mb: 0.5 }}>
                      {sub.product_title} ({planDays}d / {packSize}g)
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: COLORS.textMuted, mb: 1 }}>ID: {sub.id}</Typography>

                    {issues.issues.map((issue, idx) => (
                      <Box key={idx} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: issue.severity === "error" ? COLORS.red : "#FF9800" }} />
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: issue.severity === "error" ? COLORS.red : COLORS.orangeText }}>
                            {issue.message}
                          </Typography>
                        </Box>
                        <Box sx={{ ml: 2 }}>
                          {issue.details.slice(0, 3).map((detail, dIdx) => (
                            <Typography key={dIdx} sx={{ fontSize: 11, color: COLORS.textMuted }}>• {detail}</Typography>
                          ))}
                          {issue.details.length > 3 && (
                            <Typography sx={{ fontSize: 11, color: COLORS.textLight }}>... and {issue.details.length - 3} more</Typography>
                          )}
                        </Box>

                        {issue.type === "mismatch" && (
                          <Box sx={{ display: "flex", gap: 1, mt: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                            <Typography sx={{ fontSize: 11, color: COLORS.textMuted }}>Fix to:</Typography>
                            <FormControl size="small" sx={{ minWidth: 90 }}>
                              <Select
                                value={fixSelections[sub.id] || ""}
                                onChange={(e) => setFixSelections(prev => ({ ...prev, [sub.id]: e.target.value }))}
                                displayEmpty
                                sx={{ fontSize: 12, height: 32, "& .MuiSelect-select": { py: 0.5 } }}
                              >
                                <MenuItem value="" disabled><em>Select</em></MenuItem>
                                {PACK_SIZES.map(size => (
                                  <MenuItem key={size} value={String(size)} disabled={String(size) === packSize} sx={{ fontSize: 12 }}>
                                    {size}g {String(size) === packSize ? "(current)" : ""}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!fixSelections[sub.id] || fixingSubId === sub.id}
                              onClick={() => handleFix(sub)}
                              sx={{ fontSize: 11, py: 0.5, px: 1.5, textTransform: "none", bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark } }}
                            >
                              {fixingSubId === sub.id ? <CircularProgress size={14} sx={{ color: "white" }} /> : "Fix Now"}
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>

                  <Button size="small" variant="outlined" onClick={() => onOpenModal("meals", sub)} sx={{ fontSize: 11, textTransform: "none" }}>
                    Edit Meals
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Stack>
      </Collapse>
    </QuizStyledCard>
  );
}
